import api from '@/lib/api';
import type { PaginatedResponse } from '@pms/shared';

// ─── Enums (mirroring backend) ───────────────────────────────────────────────

export type MaintenanceCategory = 'electrical' | 'plumbing' | 'hvac' | 'general' | 'other';
export type MaintenanceRequestStatus = 'open' | 'in_progress' | 'completed' | 'cancelled';
/** Backend enum: urgency level. */
export type MaintenanceUrgency = 'low' | 'medium' | 'high' | 'urgent';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface MaintenanceAuditLogEntry {
  id: string;
  action: string;
  performedBy: string;
  details: Record<string, unknown> | null;
  createdAt: string;
}

export interface MaintenanceRequestData {
  id: string;
  unitId: string;
  unitName?: string | undefined;
  tenantId: string;
  category: MaintenanceCategory;
  title: string;
  description: string | null;
  assignedTo: string | null;
  assigneeName?: string | null | undefined;
  startDate: string | null;
  endDate: string | null;
  urgency: MaintenanceUrgency;
  status: MaintenanceRequestStatus;
  reason: string | null;
  parentRequestId: string | null;
  createdAt: string;
  updatedAt: string;
  auditLogs?: MaintenanceAuditLogEntry[] | undefined;
}

export interface ListMaintenanceQuery {
  propertyId?: string | undefined;
  status?: string | undefined;
  category?: string | undefined;
  assignedTo?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface CreateMaintenancePayload {
  unitId: string;
  category: MaintenanceCategory;
  title: string;
  description?: string | undefined;
  assignedTo?: string | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  urgency?: MaintenanceUrgency | undefined;
  reason?: string | undefined;
  parentRequestId?: string | undefined;
}

export interface UpdateMaintenancePayload {
  category?: MaintenanceCategory | undefined;
  title?: string | undefined;
  description?: string | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  urgency?: MaintenanceUrgency | undefined;
  status?: MaintenanceRequestStatus | undefined;
  reason?: string | undefined;
}

export interface AssignMaintenancePayload {
  assignedTo: string;
}

// ─── API functions ───────────────────────────────────────────────────────────

export async function fetchMaintenanceRequests(query?: ListMaintenanceQuery): Promise<PaginatedResponse<MaintenanceRequestData>> {
  const { data } = await api.get<PaginatedResponse<MaintenanceRequestData>>('/maintenance', { params: query });
  return data;
}

export async function createMaintenanceRequest(payload: CreateMaintenancePayload): Promise<MaintenanceRequestData> {
  const { data } = await api.post('/maintenance', payload);
  return data;
}

export async function fetchMaintenanceRequest(id: string): Promise<MaintenanceRequestData> {
  const { data } = await api.get(`/maintenance/${id}`);
  return data;
}

export async function updateMaintenanceRequest(id: string, payload: UpdateMaintenancePayload): Promise<MaintenanceRequestData> {
  const { data } = await api.patch(`/maintenance/${id}`, payload);
  return data;
}

export async function assignMaintenanceRequest(id: string, payload: AssignMaintenancePayload): Promise<MaintenanceRequestData> {
  const { data } = await api.post(`/maintenance/${id}/assign`, payload);
  return data;
}
